import React, { useEffect, useRef, useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
interface HiresRangeControlProps {
  adSpend: number;
  cpaLow: number;
  cpaHigh: number;
  onAdSpendChange: (next: number) => void;
}

/**
 * A compact version of the average monthly output control: arrows step the ad
 * spend in $2,500 increments, and the range itself can be typed as a target.
 */
export function HiresRangeControl({
  adSpend,
  cpaLow,
  cpaHigh,
  onAdSpendChange
}: HiresRangeControlProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const midCpa = (cpaLow + cpaHigh) / 2;
  const rangeLow = Math.max(1, Math.round(adSpend / cpaHigh));
  const rangeHigh = Math.max(rangeLow + 1, Math.round(adSpend / cpaLow));
  useEffect(() => {
    if (isEditing) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [isEditing]);
  const startEditing = () => {
    setDraft(String(Math.round(adSpend / midCpa)));
    setIsEditing(true);
  };
  const commit = () => {
    const parsed = Number(draft.replace(/[^0-9]/g, ''));
    if (Number.isFinite(parsed) && parsed > 0) {
      const hires = Math.min(500, Math.max(1, parsed));
      onAdSpendChange(Math.round(hires * midCpa));
    }
    setIsEditing(false);
  };
  const stepSpend = (direction: -1 | 1) => {
    const next = direction === 1 ? Math.floor(adSpend / 2500) * 2500 + 2500 : Math.ceil(adSpend / 2500) * 2500 - 2500;
    onAdSpendChange(Math.min(250000, Math.max(2500, next)));
  };
  const arrowButton = 'w-8 h-8 shrink-0 rounded-full flex items-center justify-center text-[#7FE0C3] transition-colors duration-150 ease-out hover:bg-[#7FE0C3]/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#7FE0C3]';
  return <div className="rounded-lg border border-[#7FE0C3]/30 bg-[#7FE0C3]/5 px-3 py-3 text-center" data-id="element-200" data-mp="797kdmn5sq83">
      <div className="text-[10px] font-bold uppercase tracking-widest text-[#7FE0C3] mb-1.5" data-id="element-201" data-mp="2fv8md3kwdwr">
        Average Monthly Output
      </div>
      <div className="flex items-center justify-center gap-1.5" data-id="element-202" data-mp="qc8cozflvkro">
        <button type="button" onClick={() => stepSpend(-1)} aria-label="Decrease monthly ad spend by $2,500" className={arrowButton} data-id="element-203" data-mp="qbq5j7renm4z">
          <ChevronLeftIcon className="w-5 h-5" data-id="element-204" data-mp="gl5b7wirl3rv" />
        </button>

        {isEditing ? <div className="flex items-baseline gap-2" data-id="element-205" data-mp="2m5owviq50yv">
            <input ref={inputRef} type="text" inputMode="numeric" value={draft} aria-label="Target hires per month" onChange={event => setDraft(event.target.value)} onBlur={commit} onKeyDown={event => {
          if (event.key === 'Enter') {
            commit();
          }
          if (event.key === 'Escape') {
            setIsEditing(false);
          }
        }} className="w-[4ch] bg-transparent border-b-2 border-[#7FE0C3] text-center text-2xl font-bold text-white tracking-tight outline-none" data-id="element-206" data-mp="7lqmp2m5r3a0" />
            <span className="text-xs font-semibold text-[#7FE0C3]" data-id="element-207" data-mp="3m9pd7w5syj9l">
              hires / month
            </span>
          </div> : <button type="button" onClick={startEditing} aria-label={`Average monthly output, ${rangeLow} to ${rangeHigh} hires. Click to enter a target.`} className="flex items-baseline gap-2 rounded px-1 border-b-2 border-dashed border-[#7FE0C3]/40 hover:border-[#7FE0C3] transition-colors duration-150 ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-[#7FE0C3]" data-id="element-208" data-mp="3hhxvmb4mjnvl">
            <span className="text-2xl font-bold text-white tracking-tight" data-id="element-209" data-mp="4200ycwuxtrh">
              {rangeLow}–{rangeHigh}
            </span>
            <span className="text-xs font-semibold text-[#7FE0C3]" data-id="element-210" data-mp="25vz6sir7niws">
              hires / month
            </span>
          </button>}

        <button type="button" onClick={() => stepSpend(1)} aria-label="Increase monthly ad spend by $2,500" className={arrowButton} data-id="element-211" data-mp="289ksszjol4hs">
          <ChevronRightIcon className="w-5 h-5" data-id="element-212" data-mp="2hc3wflvajl53" />
        </button>
      </div>
    </div>;
}